import { useCallback, useEffect, useState } from "react";
import type { Cue as WebVTTCue } from "webvtt-parser";

import { TranscriptPopup } from "../TranscriptPopup";

interface AudioTranscriptionsProps {
  audioPlayer: HTMLAudioElement | null;
  compact?: boolean;
  cues?: WebVTTCue[];
  currentTime?: number;
  transcriptionId: number;
  transcriptionPreview: string;
}

export const AudioTranscriptions = ({
  transcriptionId,
  transcriptionPreview,
  audioPlayer,
  currentTime,
  cues,
  compact = false,
}: AudioTranscriptionsProps) => {
  const [fallbackCurrentTime, setFallbackCurrentTime] = useState(0);

  useEffect(() => {
    if (currentTime !== undefined || !audioPlayer) {
      return;
    }

    const syncCurrentTime = () => {
      setFallbackCurrentTime(audioPlayer.currentTime);
    };

    syncCurrentTime();
    audioPlayer.addEventListener("timeupdate", syncCurrentTime);
    audioPlayer.addEventListener("seeked", syncCurrentTime);

    return () => {
      audioPlayer.removeEventListener("timeupdate", syncCurrentTime);
      audioPlayer.removeEventListener("seeked", syncCurrentTime);
    };
  }, [audioPlayer, currentTime]);

  const onSeek = useCallback(
    (time: number) => {
      if (!audioPlayer) return;

      audioPlayer.currentTime = time;
      void audioPlayer.play();
    },
    [audioPlayer],
  );

  if (!transcriptionPreview) {
    return null;
  }

  return (
    <div className="mt-4">
      <TranscriptPopup
        cues={cues ?? []}
        compact={compact}
        mediaTranscriptId={transcriptionId}
        onSeek={onSeek}
        transcriptPreview={transcriptionPreview}
        currentTime={currentTime ?? fallbackCurrentTime}
      />
    </div>
  );
};
